import React, { createContext, useContext, useState, useMemo, useRef, useEffect } from "react";
import "./AppSelect.scss";
import Icon from "./Icon";
import { useClickOutside } from "../../../onboardingpage/modalview/utils";

/**
 * Context type for sharing select state between AppSelect and AppSelectOption components
 */
interface SelectContextType {
  selectedValue: string | null;
  disabled: boolean;
  setSelectedValue: (value: string, props?: any) => void;
}

/**
 * Props for the AppSelect component
 */
interface AppSelectProps {
  /**
   * Initial selected value. If provided, must match one of the option values.
   * If not provided or invalid, defaults to the first option's value.
   */
  initialValue?: string | null;

  /**
   * Callback function triggered when selection changes
   * @param value The newly selected option's value
   */
  onChange: (value: string) => void;

  /**
   * Whether the select is disabled. When true, the select cannot be opened
   * and no options can be selected
   * @default false
   */
  disabled?: boolean;

  /**
   * AppSelectOption components to be rendered as options
   */
  children: React.ReactNode;

  /**
   * Optional custom render function for the active/selected option
   * @param optionProps The props of the currently selected option
   */
  renderActiveOption?: (optionProps?: AppSelectOptionProps) => React.ReactNode;

  /**
   * Additional CSS class names to be applied to the root element
   */
  className?: string;

  /**
   * Placeholder text to show when no option is selected
   */
  placeholder?: string;
}

/**
 * Props for individual select options
 */
interface AppSelectOptionProps {
  /**
   * Unique value for this option
   */
  value: string;

  /**
   * React element to be rendered as the option's content
   */
  children: React.ReactNode;

  /**
   * Text label for the option, used when renderActiveOption is not provided
   */
  label?: string;

  /**
   * Additional CSS class names for the option
   */
  className?: string;

  /**
   * Additional properties that will be passed to renderActiveOption
   */
  [key: string]: any;
}

const SelectContext = createContext<SelectContextType | null>(null);

/**
 * A customizable select component that supports custom rendering of options and
 * the active selection. It provides a dropdown interface with support for keyboard
 * navigation and click-outside behavior.
 *
 * The component uses a context to share state between the main select and its options,
 * allowing for flexible composition and custom rendering.
 *
 * @example Basic usage
 * ```tsx
 * <AppSelect onChange={(value) => console.log(value)}>
 *   <AppSelectOption value="1" label="Option 1">
 *     <span>Option 1</span>
 *   </AppSelectOption>
 *   <AppSelectOption value="2" label="Option 2">
 *     <span>Option 2</span>
 *   </AppSelectOption>
 * </AppSelect>
 * ```
 *
 * @example With placeholder
 * ```tsx
 * <AppSelect
 *   onChange={(value) => console.log(value)}
 *   placeholder="Select an option"
 * >
 *   <AppSelectOption value="1" label="Option 1">
 *     <span>Option 1</span>
 *   </AppSelectOption>
 *   <AppSelectOption value="2" label="Option 2">
 *     <span>Option 2</span>
 *   </AppSelectOption>
 * </AppSelect>
 * ```
 *
 * @example Single option behavior
 * ```tsx
 * // When there is only one option:
 * // 1. The dropdown will not open
 * // 2. The expand icon is not shown
 * // 3. If a placeholder is provided, it will be ignored and the option will be shown
 * <AppSelect onChange={(value) => console.log(value)}>
 *   <AppSelectOption value="1" label="Option 1">
 *     <span>Option 1</span>
 *   </AppSelectOption>
 * </AppSelect>
 * ```
 *
 * @example Custom rendering of active option
 * ```tsx
 * <AppSelect
 *   onChange={(value) => console.log(value)}
 *   renderActiveOption={(option) => (
 *     <div className="custom-active">
 *       Selected: {option?.label}
 *     </div>
 *   )}
 * >
 *   <AppSelectOption value="1" label="Option 1" icon="🌟">
 *     <span>Option 1</span>
 *   </AppSelectOption>
 * </AppSelect>
 * ```
 *
 * @example Using key prop for forced re-rendering
 * ```tsx
 * // When the select needs to be reset or re-initialized based on certain conditions,
 * // you can use the key prop to force a complete re-render
 *
 * function MyComponent() {
 *   const [currency, setCurrency] = useState("BTC");
 *
 *   return (
 *     <AppSelect
 *       key={currency} // Forces re-render when currency changes
 *       onChange={handleChange}
 *       initialValue={null} // Will reset to first option on re-render
 *     >
 *       {options.map(opt => (
 *         <AppSelectOption
 *           key={opt.value}
 *           value={opt.value}
 *           label={opt.label}
 *         >
 *           <span>{opt.label}</span>
 *         </AppSelectOption>
 *       ))}
 *     </AppSelect>
 *   );
 * }
 * ```
 */
export const AppSelect: React.FC<AppSelectProps> = ({ initialValue, onChange, disabled = false, children, renderActiveOption, className, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const options = useMemo(() => {
    const childOptions: AppSelectOptionProps[] = [];
    let hasValidChild = false;

    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child)) {
        if (child.type !== AppSelectOption) {
          console.warn("[AppSelect] Only AppSelectOption components are allowed as children. Other children will be ignored.");
        } else {
          hasValidChild = true;
          childOptions.push(child.props);
        }
      }
    });

    if (!hasValidChild) {
      console.warn("[AppSelect] No valid AppSelectOption children found. The select will not function properly.");
    }

    return childOptions;
  }, [children]);
  const hasSingleOption = options.length === 1;
  const [selectedValue, setSelectedValue] = useState<string | null>(() => {
    if (initialValue) {
      const isValidInitialValue = options.some((opt) => opt.value === initialValue);
      if (!isValidInitialValue) {
        console.warn(`[AppSelect] initialValue "${initialValue}" does not match any option values. Defaulting to first option.`);
        return options[0]?.value || null;
      }
      return initialValue;
    }
    if (placeholder && !hasSingleOption) {
      // We do not want an option to be selected if there is a placeholder unless there is only one option
      return null;
    }
    return options[0]?.value || null;
  });
  const selectRef = useRef<HTMLDivElement>(null);
  useClickOutside(selectRef, () => setIsOpen(false));

  // Trigger onChange on mount with either initialValue or first option
  useEffect(() => {
    if (selectedValue) {
      onChange(selectedValue);
    }
  }, []); // Empty dependency array ensures this only runs once on mount

  const getOptionProps = (value: string): AppSelectOptionProps | undefined => {
    const option = options.find((opt) => opt.value === value);
    if (!option && value) {
      console.warn(`[AppSelect] No option found for value: "${value}"`);
    }
    return option;
  };

  const handleChange = (value: string) => {
    try {
      if (!options.some((opt) => opt.value === value)) {
        console.error(`[AppSelect] Invalid value selected: "${value}". This should not happen.`);
        return;
      }
      if (value !== selectedValue) {
        setSelectedValue(value);
        onChange(value);
      }
      setIsOpen(false);
    } catch (err) {
      console.error("[AppSelect] Error handling value change:", err);
    }
  };

  const onToggleOpen = () => {
    if (disabled || hasSingleOption) return;
    if (options.length === 0) {
      console.warn("[AppSelect] Cannot open select with no options");
      return;
    }
    setIsOpen(!isOpen);
  };

  return (
    <SelectContext.Provider
      value={{
        selectedValue,
        disabled,
        setSelectedValue: handleChange,
      }}
    >
      <div
        ref={selectRef}
        className={`app-select ${disabled ? "app-select--disabled" : ""} ${hasSingleOption ? "app-select--not-clickable" : ""} ${className || ""}`}
        role="combobox"
        tabIndex={disabled ? -1 : 0}
      >
        <div
          className={`app-select__button ${isOpen ? "app-select__button--open" : ""}${
            disabled ? "app-select__button--disabled app-select__button--not-clickable" : ""
          } ${hasSingleOption ? "app-select__button--not-clickable" : ""}`}
          onClick={onToggleOpen}
        >
          <ActiveOption selectedValue={selectedValue} renderActiveOption={renderActiveOption} getOptionProps={getOptionProps} placeholder={placeholder} />
          {!disabled && !hasSingleOption && (
            <Icon
              name="expand"
              size={15}
              className={`app-select__expand-icon ${isOpen ? "app-select__expand-icon--open" : ""} ${disabled ? "app-select__expand-icon--disabled" : ""}`}
            />
          )}
        </div>
        <div className={`app-select__dropdown ${isOpen ? "app-select__dropdown--open" : ""}`} role="listbox">
          {children}
        </div>
      </div>
    </SelectContext.Provider>
  );
};

/**
 * Individual option component for AppSelect. Must be used as a child of AppSelect.
 * Throws an error if used outside of AppSelect context or if children is not a valid
 * React element.
 */
export const AppSelectOption: React.FC<AppSelectOptionProps> = ({ value, children, label, className }) => {
  const context = useContext(SelectContext);

  if (!context) {
    throw new Error("AppSelectOption must be used within AppSelect");
  }

  if (!React.isValidElement(children)) {
    throw new Error("AppSelectOption children must be a valid React element");
  }

  const { setSelectedValue, disabled } = context;

  return (
    <div className={`app-select__option ${className || ""}`} onClick={() => !disabled && setSelectedValue(value)} role="option" data-value={value}>
      {React.isValidElement(children) ? children : label}
    </div>
  );
};

const ActiveOption = ({
  selectedValue,
  getOptionProps,
  renderActiveOption,
  placeholder,
}: {
  selectedValue: string | null;
  getOptionProps: (value: string) => AppSelectOptionProps | undefined;
  renderActiveOption?: (props: AppSelectOptionProps) => React.ReactNode;
  placeholder?: string;
}) => {
  if (!selectedValue) return placeholder ? <div className="app-select__active-item">{placeholder}</div> : null;

  return (
    <div className="app-select__active-item">
      {renderActiveOption ? renderActiveOption(getOptionProps(selectedValue) as AppSelectOptionProps) : getOptionProps(selectedValue)?.label || ""}
    </div>
  );
};
